import { useMemo, useState } from 'react';
import type { ChatToolCall } from '../api/client';

interface ToolCallIndicatorProps {
  toolCall: ChatToolCall;
}

const TOOL_ICON_BY_PREFIX: Record<string, string> = {
  get: '🔎',
  query: '🔍',
  explain: '📘',
  compare: '⚖️',
  select: '🎯',
  validate: '✅',
  generate: '🧪',
};

function formatToolName(toolName: string): string {
  return toolName
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function getToolIcon(toolName: string): string {
  const [prefix] = toolName.split(/[-_]/);
  return TOOL_ICON_BY_PREFIX[prefix] ?? '🛠️';
}

function createResultPreview(result: string): string {
  const normalized = result.replace(/\s+/g, ' ').trim();
  if (!normalized) {
    return 'No tool output returned.';
  }

  return normalized.length > 140 ? `${normalized.slice(0, 137).trimEnd()}...` : normalized;
}

export function ToolCallIndicator({ toolCall }: ToolCallIndicatorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const toolLabel = useMemo(() => formatToolName(toolCall.toolName), [toolCall.toolName]);
  const preview = useMemo(() => createResultPreview(toolCall.result), [toolCall.result]);

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      <button
        type="button"
        onClick={() => setIsExpanded((current) => !current)}
        className="w-full px-3 py-2 text-left transition-colors hover:bg-slate-100"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
              <span aria-hidden="true">{getToolIcon(toolCall.toolName)}</span>
              <span>{toolLabel}</span>
            </div>
            <p className="mt-1 text-xs text-slate-600">{preview}</p>
          </div>
          <span className="shrink-0 text-xs font-medium text-primary-600">
            {isExpanded ? 'Hide' : 'Show'}
          </span>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-slate-200 px-3 py-3">
          <pre className="max-h-72 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
            <code>{toolCall.result}</code>
          </pre>
        </div>
      )}
    </div>
  );
}
